// src/services/documentApiService.ts
import { DefaultApi, Configuration } from "document-api-client";
import { fetchAuthSession } from "aws-amplify/auth";
import type {
    DocumentUploadUrlRequest,
    UpdateDocumentRequest,
} from "document-api-client";

class DocumentApiService {
    private api: DefaultApi;
    private basePath: string;

    constructor() {
        this.basePath =
            "https://403cdudipa.execute-api.eu-west-1.amazonaws.com";
        this.api = new DefaultApi(
            new Configuration({
                basePath: this.basePath,
            }),
        );
    }

    private async getAuthHeaders(): Promise<HeadersInit> {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        return {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        };
    }

    async listDocuments(params?: {
        limit?: string;
        nextToken?: string;
        status?: "QUEUED" | "PROCESSING" | "SUBMITTED" | "COMPLETED" | "FAILED";
        yearMonth?: string;
    }) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await this.api.v1DocumentsGet(params || {}, {
                headers,
            });
            return response;
        } catch (error) {
            console.error("listDocuments error:", error);
            throw error;
        }
    }

    async getDocument(documentId: string) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await this.api.v1DocumentsDocumentIdGet(
                { documentId },
                { headers },
            );
            return response;
        } catch (error) {
            console.error("getDocument error:", error);
            throw error;
        }
    }

    async updateDocument(
        documentId: string,
        updateRequest: UpdateDocumentRequest,
    ) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await this.api.v1DocumentsDocumentIdPut(
                { documentId, updateDocumentRequest: updateRequest },
                { headers },
            );
            return response;
        } catch (error) {
            console.error("updateDocument error:", error);
            throw error;
        }
    }

    async deleteDocument(documentId: string) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await this.api.v1DocumentsDocumentIdDelete(
                { documentId },
                { headers },
            );
            return response;
        } catch (error) {
            console.error("deleteDocument error:", error);
            throw error;
        }
    }

    async getDocumentStatus(documentId: string) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await this.api.v1DocumentsDocumentIdStatusGet(
                { documentId },
                { headers },
            );
            return response;
        } catch (error) {
            console.error("getDocumentStatus error:", error);
            throw error;
        }
    }

    async generateUploadUrl(request: DocumentUploadUrlRequest) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await this.api.v1FilesPost(
                { documentUploadUrlRequest: request },
                { headers },
            );
            return response;
        } catch (error) {
            console.error("generateUploadUrl error:", error);
            throw error;
        }
    }

    async generateDownloadUrl(fileName: string) {
        try {
            const headers = await this.getAuthHeaders();
            const response = await this.api.v1FilesFileNameGet(
                { fileName },
                { headers },
            );
            return response;
        } catch (error) {
            console.error("generateDownloadUrl error:", error);
            throw error;
        }
    }
}

export const documentApiService = new DocumentApiService();
